(() => {
  if (customElements.get('tva-gift-message')) return;

  const components = new Set();
  const lockedExpressButtons = new Map();

  let initialized = false;
  let message = '';
  let savedMessage = '';
  let savedStyle = '';
  let updateUrl = '';
  let timer;
  let pending = null;
  let status = '';
  let checkoutWaiting = false;

  const styleFor = (value) => value.trim() ? '1' : '';

  const needsSave = () =>
    message !== savedMessage ||
    styleFor(message) !== savedStyle;

  function updateExpressButtons() {
    const locked = Boolean(
      pending || needsSave() || status === 'error'
    );

    document.querySelectorAll(
      '#Cart .additional-checkout-buttons, ' +
      '#Cart .cart__dynamic-checkout-buttons, ' +
      '#Cart-Drawer .additional-checkout-buttons'
    ).forEach((element) => {
      if (locked && !lockedExpressButtons.has(element)) {
        lockedExpressButtons.set(element, element.inert);
        element.inert = true;
      }
    });

    for (const [element, previous] of lockedExpressButtons) {
      if (!locked || !element.isConnected) {
        element.inert = previous;
        lockedExpressButtons.delete(element);
      }
    }
  }

  function render() {
    components.forEach((component) => component.render());
    updateExpressButtons();
  }

  function edit(value) {
    message = value;
    status = 'saving';
    clearTimeout(timer);
    render();
    timer = setTimeout(save, 400);
  }

  function save() {
    clearTimeout(timer);

    if (pending) return pending;
    if (!initialized) return Promise.resolve(true);

    if (!needsSave() && status !== 'error') {
      status = message.trim() ? 'saved' : '';
      render();
      return Promise.resolve(true);
    }

    status = 'saving';

    pending = Promise.resolve().then(async () => {
      try {
        do {
          const sentMessage = message;
          const sentStyle = styleFor(sentMessage);

          const response = await fetch(updateUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              attributes: {
                GiftMessage: sentMessage,
                GiftNoteStyle: sentStyle
              }
            })
          });

          if (!response.ok) {
            throw new Error('Cart update failed');
          }

          const cart = await response.json();
          const attributes = cart.attributes || {};

          if (
            String(attributes.GiftMessage || '') !== sentMessage ||
            String(attributes.GiftNoteStyle || '') !== sentStyle
          ) {
            throw new Error('Cart attributes were not saved');
          }

          savedMessage = sentMessage;
          savedStyle = sentStyle;
        } while (needsSave());

        status = message.trim() ? 'saved' : '';
        return true;
      } catch (error) {
        status = 'error';
        return false;
      } finally {
        pending = null;
        render();
      }
    });

    render();
    return pending;
  }

  function showError() {
    const component = [...components].find(
      (item) => item.getClientRects().length
    );

    if (!component) return;

    const details = component.querySelector('details');
    if (details) details.open = true;

    component.querySelector('[data-gift-message-input]')?.focus();
  }

  async function beforeCheckout(resume) {
    if (checkoutWaiting) return;

    checkoutWaiting = true;
    const success = await save();
    checkoutWaiting = false;

    if (success) {
      resume();
    } else {
      showError();
    }
  }

  function isCheckoutLink(element) {
    if (!element.matches('a[href]')) return false;

    const url = new URL(element.href, window.location.href);

    return (
      url.origin === window.location.origin &&
      /\/checkout\/?$/.test(url.pathname)
    );
  }

  document.addEventListener('click', (event) => {
    if (!initialized || event.defaultPrevented) return;

    const target = event.target instanceof Element
      ? event.target.closest('button, input[type="submit"], a[href]')
      : null;

    if (!target) return;

    const checkout =
      target.name === 'checkout' ||
      target.matches('.checkout-button') ||
      isCheckoutLink(target);

    if (
      !checkout ||
      (!pending && !needsSave() && status !== 'error')
    ) {
      return;
    }

    event.preventDefault();

    beforeCheckout(() => {
      if (target.isConnected) target.click();
    });
  }, true);

  document.addEventListener('submit', (event) => {
    if (!initialized || event.defaultPrevented) return;

    const form = event.target;
    const submitter = event.submitter;

    if (!(form instanceof HTMLFormElement)) return;

    const action = new URL(form.action, window.location.href);

    const isCart =
      action.origin === window.location.origin &&
      /\/cart\/?$/.test(action.pathname);

    if (
      !isCart ||
      (!pending && !needsSave() && status !== 'error')
    ) {
      return;
    }

    event.preventDefault();

    beforeCheckout(() => {
      if (!form.isConnected) return;

      if (submitter?.isConnected && submitter.form === form) {
        form.requestSubmit(submitter);
      } else if (!submitter) {
        form.requestSubmit();
      }
    });
  }, true);

  class TVAGiftMessage extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();

      const options = {
        signal: this.controller.signal
      };

      const input = this.querySelector('[data-gift-message-input]');
      if (!input) return;

      if (!initialized) {
        message = input.value;
        savedMessage = message;
        savedStyle =
          this.querySelector('[data-gift-note-style]')?.value || '';
        updateUrl = this.dataset.updateUrl;
        initialized = true;
      }

      components.add(this);

      input.addEventListener('input', () => {
        edit(input.value);
      }, options);

      input.addEventListener('change', () => {
        if (input.value !== message) edit(input.value);
        save();
      }, options);

      this.querySelector('[data-gift-message-remove]')
        ?.addEventListener('click', () => {
          edit('');
          save();
          input.focus();
        }, options);

      this.querySelector('[data-gift-message-retry]')
        ?.addEventListener('click', () => {
          save();
        }, options);

      render();
    }

    disconnectedCallback() {
      this.controller?.abort();
      components.delete(this);
    }

    render() {
      const input = this.querySelector('[data-gift-message-input]');

      if (input && input.value !== message) {
        input.value = message;
      }

      const style = this.querySelector('[data-gift-note-style]');
      if (style) style.value = styleFor(message);

      const remove = this.querySelector('[data-gift-message-remove]');
      if (remove) remove.hidden = !message;

      const retry = this.querySelector('[data-gift-message-retry]');
      if (retry) retry.hidden = status !== 'error';

      const output = this.querySelector('[data-gift-message-status]');

      if (output) {
        const text = status
          ? this.dataset[`${status}Text`] || ''
          : '';

        if (output.textContent !== text) {
          output.textContent = text;
        }

        output.dataset.error = String(status === 'error');
      }
    }
  }

  customElements.define('tva-gift-message', TVAGiftMessage);

  new MutationObserver(updateExpressButtons).observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) window.location.reload();
  });
})();